import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";
import type { OverviewDailyPnl, OverviewEquityPoint } from "../../api/client";

/* ============================================================
 * Hand-rolled SVG chart kit for the Analytics page (2026-07-09).
 *
 * Method: the dataviz procedure — form by job, validated palette,
 * fixed mark specs (2px lines, ≤24px columns with 4px rounded
 * data-ends and 2px surface gaps, hairline gridlines), a hover
 * layer by default (crosshair on lines, per-mark tooltips on
 * columns, ≥24px hit targets, keyboard focus mirrors hover).
 *
 * Chart FILL steps were validated with the palette script against
 * the panel surface #0f141d (dark band + chroma + CVD + contrast
 * all PASS): cyan #0891b2 · green #059669 · red #dc2626.
 * Thin strokes/text keep the app tokens (SPEC §14 semantics).
 * ============================================================ */

export const CHART_FILL = {
  cyan: "#0891b2",
  green: "#059669",
  red: "#dc2626",
} as const;

const GRID = "#1f2937"; // hairline, one step off surface
const AXIS_TEXT = "fill-[#67748a]";

export function fmtEur(v: number | null | undefined, opts?: { sign?: boolean }): string {
  if (v == null || Number.isNaN(v)) return "—";
  const sign = opts?.sign && v > 0 ? "+" : "";
  return `${sign}${v.toLocaleString("en-US", { maximumFractionDigits: 0 })} €`;
}

export function fmtDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(Math.max(240, el.clientWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function niceTicks(min: number, max: number, count = 4): number[] {
  if (min === max) {
    max = min + 1;
    min = min - 1;
  }
  const span = max - min;
  const step = 10 ** Math.floor(Math.log10(span / count));
  const err = span / (count * step);
  const mult = err >= 7.5 ? 10 : err >= 3.5 ? 5 : err >= 1.5 ? 2 : 1;
  const s = step * mult;
  const ticks: number[] = [];
  for (let v = Math.ceil(min / s) * s; v <= max; v += s) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

/** Shared tooltip chrome (values lead, labels follow; text is React-escaped). */
function TooltipBox({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return (
    <div
      role="status"
      className="bg-overlay/95 border-border-strong pointer-events-none absolute z-10 min-w-32 rounded-md border px-2.5 py-1.5 text-xs shadow-lg backdrop-blur"
      style={{ left: x, top: y, transform: "translate(-50%, calc(-100% - 10px))" }}
    >
      {children}
    </div>
  );
}

/* ── Equity curve — single-series line + 10% area wash + crosshair tooltip ── */

export function EquityChart({
  points,
  height = 240,
}: {
  points: OverviewEquityPoint[];
  height?: number;
}) {
  const [wrapRef, width] = useMeasuredWidth();
  const [active, setActive] = useState<number | null>(null);

  const pad = { l: 56, r: 16, t: 12, b: 22 };
  const iw = width - pad.l - pad.r;
  const ih = height - pad.t - pad.b;

  const geom = useMemo(() => {
    if (points.length === 0) return null;
    const t0 = new Date(points[0].t).getTime();
    const t1 = new Date(points[points.length - 1].t).getTime();
    const span = Math.max(1, t1 - t0);
    const values = points.map((p) => p.cumPnlEur);
    const lo = Math.min(0, ...values);
    const hi = Math.max(0, ...values);
    const range = Math.max(1, hi - lo);
    const x = (p: OverviewEquityPoint) =>
      pad.l + (points.length === 1 ? iw / 2 : ((new Date(p.t).getTime() - t0) / span) * iw);
    const y = (v: number) => pad.t + ih - ((v - lo) / range) * ih;
    return { x, y, lo, hi, xs: points.map(x) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, width, height]);

  const pick = useCallback(
    (clientX: number, rect: DOMRect) => {
      if (!geom || points.length === 0) return;
      const px = clientX - rect.left;
      let best = 0;
      let bestD = Infinity;
      geom.xs.forEach((x, i) => {
        const d = Math.abs(x - px);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      setActive(best);
    },
    [geom, points.length],
  );

  if (points.length === 0) {
    return (
      <div className="text-muted-foreground flex h-40 items-center justify-center text-sm">
        Aucun cycle réglé dans cette fenêtre.
      </div>
    );
  }
  if (!geom) return null;

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${geom.x(p).toFixed(1)},${geom.y(p.cumPnlEur).toFixed(1)}`)
    .join(" ");
  const floor = geom.y(geom.lo);
  const area =
    `${path} L${geom.x(points[points.length - 1]).toFixed(1)},${floor.toFixed(1)}` +
    ` L${geom.x(points[0]).toFixed(1)},${floor.toFixed(1)} Z`;
  const ticks = niceTicks(geom.lo, geom.hi);
  const last = points[points.length - 1];
  const a = active != null ? points[active] : null;

  return (
    <div ref={wrapRef} className="relative" data-testid="equity-chart">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Courbe d'equity, ${points.length} cycles réglés, P&L cumulé ${fmtEur(last.cumPnlEur, { sign: true })}`}
        tabIndex={0}
        className="focus:outline-none focus-visible:ring-1 focus-visible:ring-[--color-accent-neon]"
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerLeave={() => setActive(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight")
            setActive((v) => Math.min(points.length - 1, (v ?? -1) + 1));
          else if (e.key === "ArrowLeft") setActive((v) => Math.max(0, (v ?? points.length) - 1));
          else if (e.key === "Escape") setActive(null);
        }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={width - pad.r} y1={geom.y(t)} y2={geom.y(t)} stroke={GRID} strokeWidth={1} />
            <text x={pad.l - 8} y={geom.y(t) + 3.5} textAnchor="end" className={cn("font-mono text-[10px] tabular-nums", AXIS_TEXT)}>
              {t.toLocaleString("en-US")}
            </text>
          </g>
        ))}
        {geom.lo < 0 && geom.hi > 0 && (
          <line x1={pad.l} x2={width - pad.r} y1={geom.y(0)} y2={geom.y(0)} stroke="#36425a" strokeWidth={1} />
        )}
        <path d={area} fill="#22d3ee" opacity={0.1} />
        <path d={path} fill="none" stroke="#22d3ee" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* end marker: ≥8px dot with a 2px surface ring */}
        <circle cx={geom.x(last)} cy={geom.y(last.cumPnlEur)} r={6} fill="#0f141d" />
        <circle cx={geom.x(last)} cy={geom.y(last.cumPnlEur)} r={4} fill="#22d3ee" />
        {a && (
          <g>
            <line x1={geom.x(a)} x2={geom.x(a)} y1={pad.t} y2={height - pad.b} stroke="#36425a" strokeWidth={1} />
            <circle cx={geom.x(a)} cy={geom.y(a.cumPnlEur)} r={6} fill="#0f141d" />
            <circle cx={geom.x(a)} cy={geom.y(a.cumPnlEur)} r={4} fill="#22d3ee" />
          </g>
        )}
        <text x={pad.l} y={height - 6} className={cn("font-mono text-[10px]", AXIS_TEXT)}>
          {fmtDay(points[0].t)}
        </text>
        <text x={width - pad.r} y={height - 6} textAnchor="end" className={cn("font-mono text-[10px]", AXIS_TEXT)}>
          {fmtDay(last.t)}
        </text>
      </svg>
      {a && (
        <TooltipBox x={geom.x(a)} y={geom.y(a.cumPnlEur)}>
          <div className="text-foreground font-mono text-sm font-semibold tabular-nums">
            {fmtEur(a.cumPnlEur, { sign: true })}
          </div>
          <div className="text-muted-foreground mt-0.5 flex items-center justify-between gap-3">
            <span>cycle #{a.cycleId}</span>
            <span className={cn("font-mono tabular-nums", a.pnlEur >= 0 ? "text-buy" : "text-sell")}>
              {fmtEur(a.pnlEur, { sign: true })}
            </span>
          </div>
          <div className="text-dim mt-0.5">{new Date(a.t).toLocaleString("en-GB")}</div>
        </TooltipBox>
      )}
    </div>
  );
}

/* ── Daily P&L — diverging columns (green/red), per-mark tooltip, 2px gaps ── */

export function DailyPnlColumns({
  days,
  height = 110,
}: {
  days: OverviewDailyPnl[];
  height?: number;
}) {
  const [wrapRef, width] = useMeasuredWidth();
  const [active, setActive] = useState<number | null>(null);

  const pad = { l: 56, r: 16, t: 6, b: 18 };
  const iw = width - pad.l - pad.r;
  const ih = height - pad.t - pad.b;

  if (days.length === 0) return null;

  const lo = Math.min(0, ...days.map((d) => d.pnlEur));
  const hi = Math.max(0, ...days.map((d) => d.pnlEur));
  const range = Math.max(1, hi - lo);
  const y = (v: number) => pad.t + ih - ((v - lo) / range) * ih;
  const zero = y(0);
  // ≤24px columns, 2px surface gap; the hit target is the full band (≥24px wide).
  const band = iw / days.length;
  const bar = Math.min(24, Math.max(3, band - 2));
  const a = active != null ? days[active] : null;

  return (
    <div ref={wrapRef} className="relative" data-testid="daily-pnl-columns">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`P&L quotidien, ${days.length} jours de bourse`}
        onPointerLeave={() => setActive(null)}
      >
        <line x1={pad.l} x2={width - pad.r} y1={zero} y2={zero} stroke="#36425a" strokeWidth={1} />
        <text x={pad.l - 8} y={zero + 3.5} textAnchor="end" className={cn("font-mono text-[10px] tabular-nums", AXIS_TEXT)}>
          0
        </text>
        {days.map((d, i) => {
          const cx = pad.l + band * i + band / 2;
          const pos = d.pnlEur >= 0;
          const top = pos ? y(d.pnlEur) : zero;
          const h = Math.max(1, Math.abs(y(d.pnlEur) - zero));
          const r = Math.min(4, bar / 2, h);
          // 4px rounded DATA end, square at the baseline.
          const dPath = pos
            ? `M${cx - bar / 2},${zero} V${top + r} Q${cx - bar / 2},${top} ${cx - bar / 2 + r},${top} H${cx + bar / 2 - r} Q${cx + bar / 2},${top} ${cx + bar / 2},${top + r} V${zero} Z`
            : `M${cx - bar / 2},${zero} V${top + h - r} Q${cx - bar / 2},${top + h} ${cx - bar / 2 + r},${top + h} H${cx + bar / 2 - r} Q${cx + bar / 2},${top + h} ${cx + bar / 2},${top + h - r} V${zero} Z`;
          return (
            <g key={d.date}>
              <path
                d={dPath}
                fill={pos ? CHART_FILL.green : CHART_FILL.red}
                opacity={active == null || active === i ? 1 : 0.45}
              />
              <rect
                x={pad.l + band * i}
                y={pad.t}
                width={band}
                height={ih}
                fill="transparent"
                onPointerMove={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                tabIndex={0}
                role="graphics-symbol"
                aria-label={`${d.date}: ${fmtEur(d.pnlEur, { sign: true })}, ${d.cycles} cycles`}
              />
            </g>
          );
        })}
        <text x={pad.l} y={height - 4} className={cn("font-mono text-[10px]", AXIS_TEXT)}>
          {fmtDay(days[0].date)}
        </text>
        <text x={width - pad.r} y={height - 4} textAnchor="end" className={cn("font-mono text-[10px]", AXIS_TEXT)}>
          {fmtDay(days[days.length - 1].date)}
        </text>
      </svg>
      {a && active != null && (
        <TooltipBox x={pad.l + band * active + band / 2} y={a.pnlEur >= 0 ? y(a.pnlEur) : zero}>
          <div className={cn("font-mono text-sm font-semibold tabular-nums", a.pnlEur >= 0 ? "text-buy" : "text-sell")}>
            {fmtEur(a.pnlEur, { sign: true })}
          </div>
          <div className="text-muted-foreground mt-0.5">
            {a.date} · {a.cycles} cycle{a.cycles > 1 ? "s" : ""}
          </div>
        </TooltipBox>
      )}
    </div>
  );
}

/* ── Horizontal share bars (emphasis form) — used for the exit mix ────────── */

export interface ShareBarItem {
  key: string;
  label: string;
  count: number;
  /** Right-aligned secondary value (already formatted). */
  detail?: string;
  /** true → accent fill (the emphasized entity); false → de-emphasis gray. */
  emphasis?: boolean;
}

export function ShareBars({ items, testId }: { items: ShareBarItem[]; testId?: string }) {
  const total = items.reduce((s, i) => s + i.count, 0);
  if (total === 0) {
    return <div className="text-muted-foreground text-sm">Aucune position fermée dans cette fenêtre.</div>;
  }
  return (
    <div className="flex flex-col gap-2.5" data-testid={testId}>
      {items.map((it) => {
        const pct = (it.count / total) * 100;
        return (
          <div key={it.key} title={`${it.label}: ${it.count} (${pct.toFixed(0)}%)`}>
            <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-foreground truncate">{it.label}</span>
              <span className="text-muted-foreground font-mono whitespace-nowrap tabular-nums">
                {it.count} · {pct.toFixed(0)}%{it.detail ? ` · ${it.detail}` : ""}
              </span>
            </div>
            <div className="bg-surface-2 h-2 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(1.5, pct)}%`,
                  background: it.emphasis ? CHART_FILL.cyan : "#36425a",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Diverging role bars — “does each V6 role pay?” ───────────────────────── */
